import { Method } from 'axios';
import { apiCall } from '@/utility/api/apiUtils';
import {
  API_METHOD_GET,
  API_JOB_UNASSIGNED_ITEMS_FOR_INVOICE,
} from '@/constants/apiConstants';

/**
 * Job line items that belong to no phase, shaped exactly like a phase block so the invoice picker can
 * render them through the existing PhaseWrapper -> SectionCard chain.
 *
 * The block id is the literal sentinel 'unassigned-items' rather than a uuid — the backend emits it, and
 * the frontend keys on the string.
 */

// A single unphased job product. Mirrors SectionTreeBuilder's product payload; is_invoiced is emitted
// only on this (null-phase) branch, so it does not exist on a phase block's products.
export interface UnassignedItem {
  uuid: string;
  section_id: string | null;
  product_id: string | null;
  supplier_id: string | null;
  item_type: number;
  item_code: string | null;
  manufacture_number: string | null;
  part_number: string | null;
  barcode: string | null;
  name: string;
  description: string | null;
  attachment_url: string | null;
  unit_cost: number;
  unit_price: number;
  quantity: number;
  total: number;
  display_order: number;
  markup: number;
  tax: number;
  discount: number;
  reserved_quantity_main: number;
  reserved_quantity_van: number;
  is_locked: boolean;
  is_invoiced: boolean;
}

export interface UnassignedSection {
  uuid: string;
  title: string;
  description: string;
  section_type: number;
  display_order: number;
  is_active: boolean;
  is_approved: boolean;
  parent_section_id: number | null;
  column_visibility: Record<string, boolean> | null;
  sections: UnassignedSection[];
  products: UnassignedItem[];
  total_items: number | null;
  subtotal: number | null;
  cost: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface UnassignedItemsBlock {
  // Always the sentinel 'unassigned-items'.
  id: string;
  title: string;
  is_invoiced: boolean;
  has_quote: boolean;
  sections: UnassignedSection[];
}

export interface UnassignedItemsApiResponse {
  status: string;
  data: UnassignedItemsBlock;
}

export const fetchUnassignedItemsForInvoice = async (
  jobId: string,
): Promise<UnassignedItemsApiResponse> => {
  const method: Method = API_METHOD_GET;
  const url = API_JOB_UNASSIGNED_ITEMS_FOR_INVOICE(jobId);

  try {
    const response = await apiCall({
      method,
      url,
    });

    return response;
  } catch (error) {
    console.error('Error fetching unassigned items for invoice:', error);
    throw error;
  }
};
